import { get, groupBy } from "lodash";
import React from "react";
import { Form as FinalForm } from "react-final-form";
import { connect } from "react-redux";
import { Prompt, RouteComponentProps } from "react-router-dom";
import { Col, Container, Row, Spinner } from "reactstrap";
import CheckboxField from "../../../../../components/Form/CheckboxFieldV2/CheckboxFieldV2";
import NumberField from "../../../../../components/Form/NumberFormatFieldV2/NumberFormatFieldV2";
import SingleSelectField from "../../../../../components/Form/SingleSelectFieldV2/SingleSelectField";
import TextField from "../../../../../components/Form/TextFieldV2/TextFieldV2";
import ToggleField from "../../../../../components/Form/ToggleFieldV2/ToggleFieldV2";
import * as S from "../../../../../constants/StringConstants";
import { IProposal } from "../../../../../store/models/proposal.model";
import { IRootState } from "../../../../../store/reducers";
import { fetchRiders } from "../../../../../store/reducers/proposalOptions";
import { addDefaultOption, isEmptyOrDefault } from "../../../../../util/utils.defaultValues";
import "./CaseDesign.scss";
import { saveSymbol, stepIndexSymbol, stepSymbol } from "./ProposalBuilder";

export interface IProposalDetailsCaseDesignProps extends StateProps, DispatchProps, RouteComponentProps {
  proposal: IProposal;
  productTypeID: number;
  next: (values: any) => void;
  generatePDF: (values: any) => void;
  previous: (values: any) => void;
  step: (values: any, pageIndex: number) => void;
}

export class ProposalDetailsCaseDesign extends React.Component<IProposalDetailsCaseDesignProps> {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    const { productTypeID } = this.props;
    this.props.fetchRiders(productTypeID);
  }

  handleSubmit = values => {
    const { next, previous, generatePDF, step } = this.props;
    const { formAction, ...rest } = values;

    if (window[saveSymbol]) {
      generatePDF(rest);
      window[saveSymbol] = false;
    } else if (window[stepSymbol]) {
      step(rest, window[stepIndexSymbol]);
      window[stepSymbol] = false;
    } else {
      if (formAction === "next") {
        next(rest);
      } else {
        previous(rest);
      }
    }
  };

  handleValidate = values => {
    const errors = {};

    if (isEmptyOrDefault(values.productTypeID)) {
      errors["productTypeID"] = S.FORM_FIELD_REQUIRED;
    }

    return errors;
  };

  render() {
    const {
      caseDesignOptions,
      caseDesignRiderOptions,
      proposal,
      isSubmitting,
      isSavingNext,
      isSavingPrevious,
      isClassificationLoaded,
    } = this.props;

    const riders = get(caseDesignRiderOptions, "riders", []);
    const riderBuckets = Object.values(groupBy(riders, rider => rider.sectionIndex));

    const bpOptions = addDefaultOption("", caseDesignOptions.selectBPList);
    const epOptions = addDefaultOption("", caseDesignOptions.selectEPList);
    const payorOptions = addDefaultOption("", caseDesignOptions.selectPayorList);

    return (
      <FinalForm onSubmit={this.handleSubmit} validate={this.handleValidate} initialValues={proposal}>
        {formProps => {
          const form = formProps.form;
          const isMaxBenefitFieldState = form.getFieldState("isMaxBenefit");
          const isMaxBenefit = get(isMaxBenefitFieldState, "value");
          return (
            <form className="case-design__wrap" onSubmit={formProps.handleSubmit} id="proposal-builder-form-3">
              <Prompt when={formProps.dirty && !formProps.submitting} message={S.MOD_DIALOG_LEAVING} />
              <Container>
                <Row>
                  <Col>
                    <h1 className="form-title">{S.PBCD_PRODUCT_SELECTION_TITLE}</h1>
                  </Col>
                </Row>
                <Row>
                  <Col sm="6">
                    <TextField label={S.PBCD_PRODUCT_TYPE_LABEL} name="productType" readOnly disabled />
                  </Col>
                </Row>
                <Row>
                  <Col sm="3">
                    <SingleSelectField
                      key="dc-bp-field"
                      name={"bpTypeID"}
                      label={S.PBCD_BP_LABEL}
                      options={bpOptions}
                    />
                  </Col>
                  <Col sm="3">
                    <SingleSelectField
                      key="dc-ep-field"
                      name={"epTypeID"}
                      label={S.PBCD_EP_LABEL}
                      options={epOptions}
                    />
                  </Col>
                  <Col sm="3">
                    <SingleSelectField
                      key="dc-payor-field"
                      name={"payorTypeID"}
                      label={S.PBCD_PAYOR_LABEL}
                      options={payorOptions}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm="3">
                    <div className="case-design__toggle">
                      <ToggleField key={"max-benefit-field"} name={"isMaxBenefit"} label={"Max Benefit"} />
                    </div>
                  </Col>
                  <Col sm="3">
                    {!isMaxBenefit && (
                      <NumberField
                        key={"benefit-amount-field"}
                        name={"benefitAmount"}
                        label={S.PBCD_BENEFIT_AMOUNT}
                        thousandSeparator={true}
                        decimalScale={2}
                        prefix={"$"}
                      />
                    )}
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col sm="6">
                    <TextField key={"clientNote"} name={"clientNote"} label={S.PBCD_ADDITIONAL_CLIENT_NOTE} />
                  </Col>
                </Row>
                <Row>
                  {isClassificationLoaded
                    ? riderBuckets.map((riders: any[]) => (
                        <Row>
                          {riders
                            .sort((a, b) => a.sortIndex - b.sortIndex)
                            .map(r => {
                              return (
                                <Col key={r.key} sm="4" className="case-design__riders">
                                  <CheckboxField
                                    label={r.key}
                                    name={`riders[]`}
                                    value={r.value}
                                    labelInfo={r.description}
                                  />
                                </Col>
                              );
                            })}
                        </Row>
                      ))
                    : null}
                </Row>
              </Container>
              <div className="buttons buttons__wrap">
                <div className="button-flex">
                  <button
                    className="button-v2 button-v2__secondary"
                    type="submit"
                    disabled={isSubmitting || !isClassificationLoaded}
                    onClick={e => {
                      formProps.form.change("formAction", "previous");
                    }}>
                    {isSavingPrevious || !isClassificationLoaded ? <Spinner color="light" /> : S.PC_PREV_BUTTON}
                  </button>
                  <button
                    className="button-v2 primary"
                    type="submit"
                    disabled={isSubmitting || !isClassificationLoaded}
                    onClick={e => {
                      formProps.form.change("formAction", "next");
                    }}>
                    {isSavingNext || !isClassificationLoaded ? <Spinner color="light" /> : S.PC_NEXT_BUTTON}
                  </button>
                </div>
              </div>
            </form>
          );
        }}
      </FinalForm>
    );
  }
}

const mapStateToProps = (state: IRootState) => ({
  caseDesignOptions: state.proposalOptions.caseDesignOptions,
  caseDesignRiderOptions: state.proposalOptions.caseDesignRiderOptions,
  selectProductList: state.proposalOptions.overviewOptions.selectProductList,
  isSubmitting: state.proposalOptions.isSubmitting,
  isSavingNext: state.proposalOptions.isSavingNext,
  isSavingPrevious: state.proposalOptions.isSavingPrevious,
  isClassificationLoaded: state.proposalOptions.isClassificationLoaded,
});

const mapDispatchToProps = {
  fetchRiders,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(ProposalDetailsCaseDesign);
